import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  Signal,
  ViewChild,
  WritableSignal,
  effect,
  signal,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DialogComponent } from '../../shared/components/dialog/dialog.component';
import { ChargingSession } from '../../shared/models/bff/charging-session.interface';
import {
  ChargePoint,
  StationDataInterface,
  Tariff,
} from '../../shared/models/bff/station-data.interface';
import {
  GoogleAnalyticsAction,
  GoogleAnalyticsCategory,
} from '../../shared/services/google-analytics/google-analytics.model';
import { GoogleAnalyticsService } from '../../shared/services/google-analytics/google-analytics.service';
import { LoadingIndicatorService } from '../../shared/services/loading-indicator.service';
import { SessionService } from '../../shared/services/session.service';
import { PersistenceService } from '../../shared/services/persistence.service';

@Component({
  selector: 'app-charging-overlay',
  templateUrl: './charging-overlay.component.html',
  styleUrls: ['./charging-overlay.component.scss'],
  standalone: false,
})
export class ChargingOverlayComponent implements OnInit {
  private readonly persistence: PersistenceService = inject(PersistenceService);
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly loadingIndicatorService: LoadingIndicatorService =
    inject(LoadingIndicatorService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly googleAnalyticsService: GoogleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly focusTrapFactory: FocusTrapFactory = inject(FocusTrapFactory);

  expanded: WritableSignal<boolean> = signal(true);
  index = 0;

  chargingSession: ChargingSession;
  stationData: StationDataInterface;
  chargePoint: ChargePoint;
  tariff: Tariff;
  sessionId: boolean;
  carouselInitialized = false;
  showConsumptionSlide = false;

  @ViewChild('focusTrapElement', { static: false }) focusTrapElement: ElementRef | undefined;
  private focusTrap: FocusTrap | undefined;
  public readonly sessionStopFailed$: Signal<boolean>;

  constructor() {
    this.sessionStopFailed$ = this.sessionService.sessionStopFailed$;
    effect(() => {
      if (this.expanded() && this.chargingSession) {
        this.enableFocusTrap();
      } else {
        this.disableFocusTrap();
      }
    });
  }

  ngOnInit() {
    this.sessionId = this.sessionService.getSessionExistence();

    this.sessionService.getSessionExistenceSubscription().subscribe(exists => {
      this.sessionId = exists;
    });

    this.sessionService.getSessionSubscription().subscribe((session: ChargingSession) => {
      if (!session) {
        this.chargingSession = null;
        this.sessionId = null;
        this.carouselInitialized = false;
      } else if (!this.chargingSession || this.chargingSession.date !== session.date) {
        // if there was no session or it was outdated
        const stationContext = this.persistence.getStationData();
        this.stationData = stationContext.chargingStation;
        this.chargePoint = stationContext.selectedChargePoint;
        this.tariff = JSON.parse(localStorage.getItem('tariff'));
        this.enableFocusTrap();
      }
      this.chargingSession = session;
      if (this.expanded() && this.chargingSession && !this.carouselInitialized) {
        this.initCarousel();
        this.carouselInitialized = true;
      } else if (
        this.expanded() &&
        this.chargingSession &&
        this.chargingSession.consumption === null &&
        this.showConsumptionSlide
      ) {
        this.initCarousel();
      } else if (
        this.expanded() &&
        this.chargingSession &&
        this.chargingSession.consumption !== null &&
        !this.showConsumptionSlide
      ) {
        this.initCarousel();
      }
      this.cdr.detectChanges();
    });
  }

  toggleOverlay(expand: boolean) {
    if (this.chargingSession) {
      this.expanded.set(expand);
      this.sessionService.setOverlayCollapsed(!this.expanded());
      this.cdr.detectChanges();
      if (expand) {
        this.initCarousel();
      }
    }
  }

  initCarousel() {
    if (this.chargingSession && !this.chargingSession.terminated) {
      this.showConsumptionSlide = this.chargingSession && this.chargingSession.consumption !== null;
      this.cdr.detectChanges();
    }
  }

  stopCharging() {
    this.ngZone.run(() => {
      const DialogRef = this.dialog.open(DialogComponent, {
        data: {
          headline: 'session.dialog.headline',
          button1: 'session.dialog.yes',
          button2: 'session.dialog.no',
        },
      });

      DialogRef.afterClosed().subscribe(result => {
        if (result === 'yes') {
          this.googleAnalyticsService.sendEvent(
            GoogleAnalyticsCategory.SESSION,
            GoogleAnalyticsAction.SESSION_STOPPED,
            'Stop button clicked'
          );
          this.loadingIndicatorService.setIndicator(true);
          this.sessionService.stopSession();
        }
      });
    });
  }

  clearSession() {
    this.sessionService.clearSession();
    this.expanded.set(true);
    this.index = 0;
    this.chargingSession = null;
    this.sessionId = null;
    this.stationData = null;
    this.chargePoint = null;
    this.tariff = null;
  }

  private enableFocusTrap(): void {
    setTimeout(() => {
      // wait for the view to render the expanded state
      if (!this.focusTrap) {
        this.focusTrap = this.focusTrapFactory.create(this.focusTrapElement.nativeElement);
      }
      this.focusTrap.focusFirstTabbableElement();
    });
  }

  private disableFocusTrap(): void {
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  }
}
